'use client';

import { useEffect, useRef } from 'react';
import { Crosshair } from 'lucide-react';
import type { ManualFlyTarget } from './ManualMapControl';

/**
 * Confirm a `FLY TO HERE` target — by SHOWING the imprecision, not hiding it.
 *
 * The operator chose to keep this command on small surfaces rather than disable
 * it there, and that is only safe if the confirmation states what the tap could
 * not have been precise about. So the bar leads with distance and bearing, says
 * the altitude and the speed it will fly, and prints `±N M AT THIS ZOOM` —
 * computed by `flyToHereGate` from the surface's real scale, never a literal.
 *
 * A BAR, deliberately, not a dialog over the map. A dialog would cover exactly
 * what the operator has to look at before agreeing: the pin, the obstacles
 * around it, and the aircraft it is a leg from. This is a sibling strip of the
 * command bar, the same shape and the same place as `GotoAltitudeBar` and
 * `ManualFlightBanner`, for the same reason all three are — the command bar is a
 * fixed toolbar whose key positions must not move.
 *
 * The pin stays draggable while this is open and NOTHING is sent until CONFIRM.
 * That is the whole point of the flow: the long press proposes, the operator
 * refines, and only then does a `goto` go out — through the existing verb and
 * its existing ack, with no new backend path.
 *
 * **Confirming ENDS the manual session**, and the bar says so in both the copy
 * and the key. The RC will not accept a `goto` while it holds a manual session
 * (`MissionController`: "release it before commanding goto"), so the COP hands
 * authority back first and only then dispatches. That is a real consequence for
 * the operator — the sticks are not theirs afterwards — and a confirmation that
 * hid it would be a confirmation of something else.
 */

/** Said on the strip and, verbatim, in the confirm key's accessible name. */
export const MANUAL_FLY_TO_ENDS_MANUAL =
  'Confirming ends manual control. The RC refuses a fly-here while you hold the sticks, so authority is handed back first.';

/**
 * The other half of the trade: the leg may not fly at all once it is made.
 *
 * `MissionController` routes `goto` through `if (!_armed) holdMotorCommand(env)`,
 * and a manual grant never arms — so an aircraft flown up on the RC's own sticks,
 * or one where ABORT was pressed before manual control was taken, HOLDS the leg
 * for a physical ARM. By then the release has already happened and cannot be
 * taken back. An operator agreeing to give up the sticks is owed that before the
 * press rather than after it.
 *
 * Said whenever the COP cannot see that the aircraft is armed, which for a DJI
 * companion aircraft — the only kind manual flight supports — is always: the
 * companion app publishes no `armed` field, and its `_armed` flag lives on the
 * RC. `undefined` therefore means "no source reports it", never "disarmed", and
 * the two get different sentences because they are different facts.
 */
export function manualFlyToArmCaution(armed: boolean | undefined): string | null {
  if (armed === true) return null;
  if (armed === false) {
    return 'This drone reports that it is disarmed. The RC will hold the move until someone presses Arm, and manual control will have been handed back by then.';
  }
  return 'If the drone was not armed from the air picture, the RC holds the move until someone presses Arm, and manual control will have been handed back by then. This drone does not report whether it is armed.';
}

/**
 * The pending pin, which lives in `ManualMapControl` rather than in this bar.
 *
 * It is the same proposal this strip is asking about, and an operator who
 * dropped it with `F` and refined it with the arrow keys still has focus on it
 * — its own label promises "Enter confirms". The two components are siblings
 * under the COP page and neither is handed a node by the other, so the pin is
 * matched by the attribute it actually carries.
 */
const MANUAL_FLY_PIN_SELECTOR = '[data-testid="manual-fly-pin"]';

export interface ManualFlyToHereBarProps {
  callsign: string;
  target: ManualFlyTarget;
  /** Release manual authority, then dispatch the GO-TO. The caller clears the target. */
  onConfirm: () => void;
  /** Drop the target, send nothing, keep flying. */
  onCancel: () => void;
  /** True from the moment CONFIRM is pressed — through the release AND the POST. */
  busy?: boolean;
  /**
   * The aircraft's armed state as telemetry reports it, or undefined when
   * nothing reports it. Drives `manualFlyToArmCaution` — see there for why
   * undefined is the normal case and is not read as disarmed.
   */
  armed?: boolean;
}

export function ManualFlyToHereBar({
  callsign,
  target,
  onConfirm,
  onCancel,
  busy = false,
  armed,
}: ManualFlyToHereBarProps) {
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  /**
   * Escape cancels from anywhere. Enter confirms only from the pin or the key.
   *
   * ESCAPE is registered in the CAPTURE phase on the window and stops
   * propagation, because Escape already means something while a manual session
   * is live: the command bar's listener hands authority back. With a pending
   * target on the map, Escape must clear the target and nothing else — an
   * operator dismissing a confirmation has not asked to give up the aircraft.
   * Capture is what makes that deterministic rather than a matter of listener
   * registration order. Claiming it globally is safe because all it can destroy
   * is a proposal, and the bar labels the key.
   *
   * ENTER is not safe that way and is not claimed that way. Confirming releases
   * manual authority AND dispatches a GO-TO — one press, irreversible — and
   * while a pin is up the operator's focus is usually nowhere near this strip:
   * `ManualMapControl` calls `preventDefault` on the pin's pointerdown, so a
   * dragged pin never takes focus and it stays on whatever was last clicked
   * (MANUAL, RINGS, a layout key). A global Enter therefore turned "press the
   * key I am already on again" into "hand the aircraft back and fly it
   * somewhere", and it beat a focused STAY in `ManualExitConfirm` for the same
   * reason. So Enter is claimed for exactly the two elements that ARE this
   * decision — the pending pin and the confirm key — and left alone everywhere
   * else, CANCEL included, where the browser's own activation of the focused
   * control is already the right answer and the only one the operator aimed at.
   *
   * Neither branch touches SPACE. It is the emergency stop and belongs to the
   * flight keys.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const source = event.target as HTMLElement | null;
      // A confirmation must never swallow a key the operator is typing into a
      // field somewhere else on the COP.
      if (source?.closest?.('input, textarea, select, [contenteditable="true"]')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCancelRef.current();
        return;
      }
      if (event.key === 'Enter') {
        // Where the operator's hands are, not where the event was dispatched.
        const focused = document.activeElement as HTMLElement | null;
        const onTheTarget = focused?.closest?.(MANUAL_FLY_PIN_SELECTOR) != null;
        const onTheKey = focused !== null && focused === confirmRef.current;
        if (!onTheTarget && !onTheKey) return;
        event.preventDefault();
        event.stopPropagation();
        if (!busyRef.current) onConfirmRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const bearing =
    target.bearingDeg === null
      ? '---'
      : String(Math.round(target.bearingDeg)).padStart(3, '0');

  const armCaution = manualFlyToArmCaution(armed);

  return (
    <div
      role="group"
      aria-label={`Confirm fly-to target for ${callsign}`}
      data-testid="manual-fly-to-here-bar"
      className="rounded-lg border border-status-blue/50 bg-card px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span
          role="status"
          aria-live="polite"
          className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] text-status-blue-fg"
        >
          <Crosshair className="size-3 shrink-0" strokeWidth={1.5} />
          Fly to here · {callsign} · not sent yet
        </span>

        <span className="font-mono tabular-nums text-[11px] tracking-[0.06em] text-foreground">
          {target.distanceM.toFixed(1)} m · BRG {bearing}°
        </span>

        <span className="font-mono tabular-nums text-[11px] tracking-[0.06em] text-foreground">
          ALT {target.altitudeM.toFixed(1)} m AGL · {target.speedMs.toFixed(1)} m/s
        </span>

        {/* The imprecision, stated. This is why the confirmation exists at all,
            so it is rendered as prominently as the distance it qualifies. */}
        <span
          data-testid="manual-fly-uncertainty"
          className="rounded border border-status-amber/40 bg-status-amber/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-status-amber-fg"
        >
          {target.uncertaintyLabel}
        </span>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            data-testid="manual-fly-cancel"
            onClick={onCancel}
            className="min-h-11 rounded border border-subtle bg-muted/40 px-3 py-1 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Cancel
            <kbd className="ml-1.5 text-[9px] tracking-wider">ESC</kbd>
          </button>
          {/* "Release & fly", not "Confirm": the key does two things and the
              operator is agreeing to both. A key labelled for only the half
              they asked for would be the surprise this bar exists to prevent. */}
          <button
            type="button"
            ref={confirmRef}
            data-testid="manual-fly-confirm"
            onClick={onConfirm}
            disabled={busy}
            className="min-h-11 rounded border border-status-blue/50 bg-status-blue/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-status-blue-fg hover:bg-status-blue/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
          >
            Release &amp; fly
            <kbd className="ml-1.5 text-[9px] tracking-wider">ENTER</kbd>
            <span className="sr-only">
              {' '}
              — {MANUAL_FLY_TO_ENDS_MANUAL}
              {armCaution ? ` ${armCaution}` : ''}
            </span>
          </button>
        </div>
      </div>

      {/* The consequence, stated where the decision is made. Amber rather than
          muted: this is the one thing on the strip an operator can be surprised
          by afterwards. */}
      <p
        data-testid="manual-fly-ends-manual"
        className="mt-1 text-[10px] leading-relaxed text-status-amber-fg"
      >
        {MANUAL_FLY_TO_ENDS_MANUAL}
      </p>

      {/* And what the aircraft may do with the leg once that trade is made. */}
      {armCaution && (
        <p
          data-testid="manual-fly-arm-caution"
          className="mt-1 text-[10px] leading-relaxed text-status-amber-fg"
        >
          {armCaution}
        </p>
      )}

      {/* The refinement affordance, in words. A pin that can be dragged but says
          so nowhere is a pin most operators will never drag. */}
      <p className="mt-1 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        Drag the pin to refine · arrow keys nudge it · nothing is sent until you confirm
      </p>
    </div>
  );
}

/**
 * What happened to the leg after the sticks were handed back — and the way back.
 *
 * A toast is the wrong home for this. The operator has just made an irreversible
 * trade and the aircraft is doing nothing: that is not a four-second notice, it
 * is a state they have to be able to read while deciding, and act on with one
 * press. So the strip stays up until it is answered or dismissed, in the same
 * place the confirmation it replaces was, and carries the two answers:
 *
 *  - **TAKE MANUAL** re-requests authority — the same request the MANUAL key
 *    makes, offered where the operator is already looking rather than sending
 *    them back to the command bar.
 *  - **DISMISS** leaves it: a leg held for an ARM is not an error, and ARM is a
 *    key on the bar. The strip must not imply the only way out is through it.
 *
 * Nothing here re-sends the GO-TO. The leg is already at the RC with an ARM
 * deadline of its own, and a second one would be a second command to fly
 * somewhere for an aircraft that has not moved on the first.
 *
 * It can also answer itself. `unconfirmed` means nothing confirmed the leg
 * within six seconds, and a slow wayline upload beats that window — so
 * `useManualFlyToOutcome` keeps following the command id and takes this strip
 * down if the aircraft starts flying after all, rather than leaving it
 * contradicting the command bar's EXECUTING readout about the same aircraft.
 */
export function ManualFlyToOutcomeNote({
  callsign,
  message,
  held,
  canTakeManual,
  onTakeManual,
  onDismiss,
}: {
  callsign: string;
  message: string;
  /** True when the RC is holding the leg for an ARM, rather than nothing having answered. */
  held: boolean;
  /** False when manual flight is refused right now — the key is shown disabled, never hidden. */
  canTakeManual: boolean;
  onTakeManual: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      data-testid="manual-fly-outcome-note"
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-status-amber/40 bg-card px-3 py-2 text-[10px] leading-relaxed text-status-amber-fg"
    >
      <span aria-hidden className="mt-px">⚠</span>
      <span className="min-w-0 flex-1">
        <span className="font-bold uppercase tracking-[0.12em]">
          {held ? 'Fly here held' : 'Fly here unconfirmed'} · {callsign}
        </span>{' '}
        — {message}
      </span>
      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          data-testid="manual-fly-retake"
          onClick={onTakeManual}
          disabled={!canTakeManual}
          className="min-h-11 rounded border border-status-blue/50 bg-status-blue/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-status-blue-fg hover:bg-status-blue/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
        >
          Take manual
        </button>
        <button
          type="button"
          data-testid="manual-fly-outcome-dismiss"
          onClick={onDismiss}
          className="min-h-11 rounded border border-subtle bg-muted/40 px-3 py-1 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
